/**
 * The frontend HITL approval tools the Portal registers via `useHumanInTheLoop`,
 * and which card shape each one drives.
 *
 * Shared by the panel (which renders their cards) and `replayMessages` (which
 * splices settled ones back into the transcript), so the two cannot disagree
 * about what counts as a gate.
 *
 * Must stay in sync with the Runtime's `_AGUI_WAITING_FRONTEND_TOOLS`
 * (`components/ag_ui_chat/handler.py`), which decides which of these park in a
 * durable interrupt. Nothing mechanically enforces that — a mismatch is silent:
 * a tool listed there but not here parks with no card to answer it, and one
 * listed here but not there shows a card whose pause dies with the tab.
 */

/**
 * Which answer shape a gate takes: the two workflow gates return `{approved}`,
 * the generic one returns `{choice}`, the Step 3a preview card returns
 * `{action}`. Lives here rather than in the panel
 * because the recovered-gate path needs the same mapping from a bare
 * `tool_name` — it has no CopilotKit render props to carry the kind.
 */
export type GateKind = 'audience' | 'content' | 'generic' | 'preview';

/**
 * The one table. `HITL_TOOL_NAMES` is derived from it so "is this a gate" and
 * "which kind of gate" cannot drift: adding a fourth gate to only one of the
 * two used to be possible, and the failure is a card that renders with the
 * wrong answer shape.
 */
export const HITL_TOOL_KINDS: Record<string, GateKind> = {
  approve_audience_segment: 'audience',
  approve_email_content: 'content',
  request_user_approval: 'generic',
  review_email_preview: 'preview',
};

export const HITL_TOOL_NAMES = new Set(Object.keys(HITL_TOOL_KINDS));

/**
 * The Runtime's reply-options tool. Not a gate: the run never parks on it and the
 * composer stays open. Its card always trails its turn (`coalesceAssistantRuns`).
 */
export const SUGGEST_REPLIES_TOOL = 'suggest_replies';

/**
 * Backend tools whose result renders as a card at its place in the transcript
 * (a name-scoped `useRenderTool`), instead of as a collapsed trace row.
 */
export const RESULT_CARD_TOOL_NAMES = new Set(['file_send', SUGGEST_REPLIES_TOOL]);

/** Tool calls that take a visible slot in the transcript: gates and result cards. */
export const INLINE_TOOL_NAMES = new Set([...HITL_TOOL_NAMES, ...RESULT_CARD_TOOL_NAMES]);

/** `undefined` for any tool that is not one of the gates. */
export function gateKindForToolName(name: string): GateKind | undefined {
  return HITL_TOOL_KINDS[name];
}

/**
 * Gates the operator may answer by typing in the composer instead of clicking.
 *
 * The typed text is sent as the gate's tool result (`dismissed: 'typed'` /
 * `action: 'typed'`) — never as a user message — so the request keeps the one
 * shape the protocol allows while a run is parked: a trailing tool result and
 * nothing after it (AG-UI interrupts Rule 4; `ag_ui_strands` rejects anything
 * else with PENDING_INTERRUPTS). The agent reads the text and decides whether it
 * is a change, a question, or neither; it is never an approval.
 *
 * Only gates whose SOP already loops back to "call this tool again" are listed.
 * Gate 2 and the generic gate stay click-only until their SOPs handle `typed`.
 */
export const TYPEABLE_GATE_KINDS: ReadonlySet<GateKind> = new Set<GateKind>(['audience', 'preview']);
